import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { browserName, browserVersion } from "react-device-detect";
import { initialStateAdmin } from "../defaultState";
import {
    getAplRole,
    getAplFeature,
    getAplRoleFeature,
    getLines,
    getPrintersDepartmentAssignments,
    getPrintersLineAssignment,
    getSites,
    getUserWorkcentreRole,
    savePrintersDepartmentAssignments,
    savePrintersLineAssignments,
    saveUserWorkcentreRole,
    saveAplRoleFeature,
    changePasswordAdmin
} from "../../aplApi/adminHelper";
import { ReduxStateAdmin } from "../../types/redux/reduxStateAdmin";
import { PrinterLineSelection } from "../../types/printerLineSelection";
import { Department } from "../../types/department";
import { UserWorkcentreRole } from "../../types/userWorkcentreRole";
import { AplRole } from "../../types/aplRole";
import { AplRoleFeature } from "../../types/aplRoleFeature";
import { getUsers, addUser } from "../../aplApi/adminHelper";
import { AplUser } from "../../types/aplUser";
import { Site } from "../../types/site";
import { LineItem } from "../../types/lineItem";
import { getPrinters } from "../../aplApi/adminHelper";
import { Printer } from "../../types/printer";
import { AplFeature } from "../../types/aplFeature";
import { IRootState } from "../../types/redux/IRootStore";
import { AplError } from "../../types/aplError";
import { getAplError } from "../../aplApi/exceptionHelper";

export const getPrintersLineAssignmentsRedux = createAsyncThunk(
    `admin/getPrintersLineAssignmentsRedux`,
    async (siteId: number, thunkApi) => {
        try {
            return (await getPrintersLineAssignment(siteId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const getPrintersDepartmentAssignmentsRedux = createAsyncThunk(
    `admin/getPrintersDepartmentAssignmentsRedux`,
    async (siteId: number, thunkApi) => {
        try {
            return (await getPrintersDepartmentAssignments(siteId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const savePrintersDepartmentAssignmentsRedux = createAsyncThunk(
    `admin/savePrintersDepartmentAssignmentsRedux`,
    async (departments: Department[], thunkApi) => {
        try {
            await savePrintersDepartmentAssignments(departments);
            return (await getPrintersDepartmentAssignments(departments[0].siteId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);
export const getAplUsersRedux = createAsyncThunk(`admin/getAplUsersRedux`, async (siteId: number, thunkApi) => {
    try {
        return (await getUsers(siteId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const addUserRedux = createAsyncThunk(`admin/addUserRedux`, async (siteId: number, thunkApi) => {
    try {
        let state = thunkApi.getState() as IRootState;
        let admin = state.admin;

        if (!admin.addUserUsername || !admin.addUserPassword || !admin.addUserSiteId) {
            let error: AplError = {
                detail: "Please fill all fields",
                status: 400,
                title: "Please fill all fields",
                aplErrorCode: null,
                type: null
            };
            return thunkApi.rejectWithValue(error);
        }

        await addUser(admin.addUserUsername, admin.addUserPassword, admin.addUserSiteId);
        return (await getUsers(siteId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const resetUserPasswordRedux = createAsyncThunk(`admin/resetUserPasswordRedux`, async (siteId: number, thunkApi) => {
    try {
        let state = thunkApi.getState() as IRootState;
        let adminRedux = state.admin;
        await changePasswordAdmin(adminRedux.selectedAplUser[0].userName, adminRedux.userPasswordReset, `${browserName}-${browserVersion}`);
        return (await getUsers(siteId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const getAplLinesRedux = createAsyncThunk(`admin/getAplLinesRedux`, async (siteId: number, thunkApi) => {
    try {
        return (await getLines(siteId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const setSelectedAplUserRedux = createAsyncThunk(`admin/setSelectedAplUserRedux`, async (aplUsers: AplUser[], thunkApi) => {
    try {
        if (aplUsers.length === 1) return (await getUserWorkcentreRole(aplUsers[0].userId)).data;
        return [];
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const saveUserWorkcentreRoleRedux = createAsyncThunk(
    `admin/saveUserWorkcentreRoleRedux`,
    async (data: { userId: string; userWorkcentreRoles: UserWorkcentreRole[] }) => {
        await saveUserWorkcentreRole(data.userId, data.userWorkcentreRoles);
        return (await getUserWorkcentreRole(data.userId)).data;
    }
);

export const getAplRolesRedux = createAsyncThunk(`admin/getAplRolesRedux`, async (_, thunkApi) => {
    try {
        return (await getAplRole()).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const getAplFeaturesRedux = createAsyncThunk(`admin/getAplFeaturesRedux`, async (_, thunkApi) => {
    try {
        return (await getAplFeature()).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const saveAplRoleFeaturesRedux = createAsyncThunk(
    `admin/saveAplRoleFeaturesRedux`,
    async (data: { roleId: number; features: AplFeature[] }, thunkApi) => {
        try {
            await saveAplRoleFeature(data.roleId, data.features);
            return (await getAplRole()).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const getAplRoleFeaturesRedux = createAsyncThunk(`admin/getAplRoleFeaturesRedux`, async () => (await getAplRoleFeature()).data);

export const getAllSitesRedux = createAsyncThunk(`admin/getAllSitesRedux`, async (siteId: number, thunkApi) => {
    try {
        return (await getSites(siteId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const getPrintersRedux = createAsyncThunk(`admin/getPrintersRedux`, async (siteId: number, thunkApi) => {
    try {
        return (await getPrinters(siteId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const savePrinterLineSelectionRedux = createAsyncThunk(
    `admin/saveUserWorkcensavePrinterLineSelectiontreRoleRedux`,
    async (data: PrinterLineSelection[], thunkApi) => {
        try {
            await savePrintersLineAssignments(data);
            return (await getPrintersLineAssignment(data[0].siteId)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

const admin = createSlice({
    name: "admin",
    initialState: initialStateAdmin,
    reducers: {
        onClearAdminRedux: () => initialStateAdmin,
        setPrinterLineAssignmentsRedux: (state, action): ReduxStateAdmin => ({
            ...state,
            printerLineSelection: action.payload,
            printerLineSelectionDirty: true
        }),
        setDepartmentPrintersRedux: (state, action): ReduxStateAdmin => ({ ...state, departments: action.payload, departmentsDirty: true }),
        setSelectedAplRoleFeatureRedux: (state, action): ReduxStateAdmin => ({ ...state, selectedAplRoleFeature: action.payload }),
        setSelectedAplRoleRedux: (state, action): ReduxStateAdmin => ({ ...state, selectedAplRole: action.payload }),
        setSelectedAplFeature: (state, action): ReduxStateAdmin => ({ ...state, selectedAplFeature: action.payload }),
        setSelectedLinesRedux: (state, action): ReduxStateAdmin => ({ ...state, selectedLines: action.payload }),
        setAddUserUsernameRedux: (state, action): ReduxStateAdmin => ({ ...state, addUserUsername: action.payload }),
        setAddUserPasswordRedux: (state, action): ReduxStateAdmin => ({ ...state, addUserPassword: action.payload }),
        setAddUserSiteId: (state, action): ReduxStateAdmin => ({ ...state, addUserSiteId: action.payload }),
        setUserWorkcentreRolesRedux: (state, action): ReduxStateAdmin => ({ ...state, userWorkcentreRoles: action.payload }),
        setSelectedAplFeatureDirtyRedux: (state, action): ReduxStateAdmin => ({ ...state, selectedAplFeatureDirty: action.payload }),
        setUserPasswordReset: (state, action): ReduxStateAdmin => ({ ...state, userPasswordReset: action.payload })
    },
    extraReducers: builder => {
        builder
            .addCase(getPrintersLineAssignmentsRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getPrintersLineAssignmentsRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    printerLineSelection: action.payload,
                    printerLineSelectionDirty: false
                } as ReduxStateAdmin;
            })
            .addCase(getPrintersLineAssignmentsRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    printerLineSelection: [] as PrinterLineSelection[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getPrintersDepartmentAssignmentsRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getPrintersDepartmentAssignmentsRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    departments: action.payload,
                    departmentsDirty: false
                } as ReduxStateAdmin;
            })
            .addCase(getPrintersDepartmentAssignmentsRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    departments: [] as Department[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(savePrintersDepartmentAssignmentsRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(savePrintersDepartmentAssignmentsRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    departments: action.payload,
                    departmentsDirty: false
                } as ReduxStateAdmin;
            })
            .addCase(savePrintersDepartmentAssignmentsRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    departments: [] as Department[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getAplUsersRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getAplUsersRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    aplUsers: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(getAplUsersRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    aplUsers: [] as AplUser[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(addUserRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(addUserRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    aplUsers: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(addUserRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(resetUserPasswordRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(resetUserPasswordRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    aplUsers: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(resetUserPasswordRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getAplLinesRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getAplLinesRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    lines: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(getAplLinesRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    lines: [] as LineItem[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });
        builder
            .addCase(setSelectedAplUserRedux.pending, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: true,
                    error: null,
                    selectedAplUser: action.meta.arg
                } as ReduxStateAdmin;
            })
            .addCase(setSelectedAplUserRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    userWorkcentreRoles: action.payload,
                    selectedAplUser: action.meta.arg
                } as ReduxStateAdmin;
            })
            .addCase(setSelectedAplUserRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    userWorkcentreRoles: [] as UserWorkcentreRole[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload,
                    selectedAplUser: action.meta.arg
                } as ReduxStateAdmin;
            });

        builder
            .addCase(saveUserWorkcentreRoleRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(saveUserWorkcentreRoleRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    userWorkcentreRoles: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(saveUserWorkcentreRoleRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    userWorkcentreRoles: [] as UserWorkcentreRole[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getAplRolesRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getAplRolesRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    aplRoles: action.payload,
                    selectedAplFeatureDirty: false
                } as ReduxStateAdmin;
            })
            .addCase(getAplRolesRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    aplRoles: [] as AplRole[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(saveAplRoleFeaturesRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(saveAplRoleFeaturesRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    aplRoles: action.payload,
                    selectedAplFeatureDirty: false
                } as ReduxStateAdmin;
            })
            .addCase(saveAplRoleFeaturesRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    aplRoles: [] as AplRole[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getAplFeaturesRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getAplFeaturesRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    aplFeatures: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(getAplFeaturesRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    aplFeatures: [] as AplFeature[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getAplRoleFeaturesRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getAplRoleFeaturesRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    aplRoleFeatures: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(getAplRoleFeaturesRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    aplRoleFeatures: [] as AplRoleFeature[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getAllSitesRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getAllSitesRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    sites: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(getAllSitesRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    sites: [] as Site[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(getPrintersRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(getPrintersRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    printers: action.payload
                } as ReduxStateAdmin;
            })
            .addCase(getPrintersRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    printers: [] as Printer[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });

        builder
            .addCase(savePrinterLineSelectionRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAdmin;
            })
            .addCase(savePrinterLineSelectionRedux.fulfilled, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    printerLineSelection: action.payload,
                    printerLineSelectionDirty: false
                } as ReduxStateAdmin;
            })
            .addCase(savePrinterLineSelectionRedux.rejected, (state, action): ReduxStateAdmin => {
                return {
                    ...state,
                    printerLineSelection: [] as PrinterLineSelection[],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAdmin;
            });
    }
});

export const {
    onClearAdminRedux,
    setPrinterLineAssignmentsRedux,
    setSelectedAplRoleRedux,
    setSelectedAplRoleFeatureRedux,
    setDepartmentPrintersRedux,
    setSelectedAplFeature,
    setAddUserUsernameRedux,
    setAddUserPasswordRedux,
    setAddUserSiteId,
    setSelectedLinesRedux,
    setUserWorkcentreRolesRedux,
    setSelectedAplFeatureDirtyRedux,
    setUserPasswordReset
} = admin.actions;
export default admin.reducer;
